import React, { useEffect } from 'react';
import {
  buildOptionsFromData,
  Checkbox,
  ErrorHandler,
  FilteredTable,
  MultiSelectTableFilter,
  DeprecatedStatePill,
  Text,
} from '@common/components';
import {
  NetworkStatus,
  useLazyQuery,
  usePaginationHelper,
  useQuery,
} from '@common/hooks';
import { PAGINATED_ORGANIZATION_ROLES_QUERY } from '@institutions/graphql';
import { capitalizeFirstLetter } from '@common/services';
import { FontSizes } from 'works/legacy/lib/style-helper';
import { RolesQuery } from '@clinician-profile/graphql';
import { CheckboxProps } from 'antd';

export function RolesAccessPaginatedTable({
  supplierId,
  selectedRowKeys,
  setSelectedRowKeys,
  rolesAccessEditionMode,
  hideStatusColumn = false,
}: {
  supplierId?: string;
  selectedRowKeys: string[];
  setSelectedRowKeys: (value: string[]) => void;
  rolesAccessEditionMode: boolean;
  hideStatusColumn?: boolean;
}) {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'SuppliersManagementRolesAccessTable',
    defaultPaginationData: {
      offset: 0,
      limit: 1000,
      orderDirection: 'ASC',
      orderField: 'NAME',
      defaultPageSize: 50,
    },
    columnsDictionary: {
      name: 'NAME',
      abbreviation: 'ABBREVIATION',
      roleType: 'ROLE_TYPE',
      enabledForSupplier: 'ENABLED_FOR_SUPPLIER',
    },
  });

  const { error, data, networkStatus } = useQuery(
    PAGINATED_ORGANIZATION_ROLES_QUERY,
    {
      variables: {
        ...paginationVariables,
        supplierId: supplierId,
      },
      notifyOnNetworkStatusChange: true,
      onCompleted() {
        fetchRoles();
      },
    }
  );

  const [fetchRoles, { error: rolesError, data: rolesData }] =
    useLazyQuery(RolesQuery);

  useEffect(() => {
    if (networkStatus === NetworkStatus.ready && data) {
      const enabledRoles = data.paginatedOrganizationRoles.roles
        .filter((role) => role.enabledForSupplier)
        .map((role) => role.id);

      setSelectedRowKeys(enabledRoles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (error || rolesError) {
    return (
      <ErrorHandler error={error} log="SuppliersManagementRolesAccessTable" />
    );
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      filtered: filters.name || false,
      filteredValue: filters.name || null,
      filters: buildOptionsFromData(
        rolesData?.roles || [],
        (role: { id: string; name: string }) => ({
          value: role.id,
          text: role.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Abbreviation',
      dataIndex: 'abbreviation',
      key: 'abbreviation',
      sorter: true,
      filtered: filters.abbreviation || false,
      filteredValue: filters.abbreviation || null,
      filters: buildOptionsFromData(
        rolesData?.roles || [],
        (role: { id: string; abbreviation: string }) => ({
          value: role.id,
          text: role.abbreviation,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Type',
      dataIndex: 'roleType',
      key: 'roleType',
      sorter: true,
      filtered: filters.roleType || false,
      filteredValue: filters.roleType || null,
      filters: [
        {
          text: 'Allied',
          value: 'allied',
        },
        {
          text: 'Locumns',
          value: 'locumns',
        },
        {
          text: 'Nursing',
          value: 'nursing',
        },
      ],
      render: (roleType: string) => capitalizeFirstLetter(roleType),
    },
    ...(!hideStatusColumn
      ? [
          {
            title: 'Status',
            dataIndex: 'enabledForSupplier',
            key: 'enabledForSupplier',
            sorter: true,
            filtered: filters.enabledForSupplier || false,
            filteredValue: filters.enabledForSupplier || null,
            filters: [
              {
                text: 'Enabled',
                value: 'enabled',
              },
              {
                text: 'Disabled',
                value: 'disabled',
              },
            ],
            render: (enabledForSupplier: boolean) => (
              <DeprecatedStatePill
                color={enabledForSupplier ? 'grass' : 'steel-lighter'}
                fontSize={FontSizes.small}
              >
                {enabledForSupplier ? 'Enabled' : 'Disabled'}
              </DeprecatedStatePill>
            ),
          },
        ]
      : []),
  ];

  const isLoaded = networkStatus === NetworkStatus.ready && data;
  const total = isLoaded ? data.paginatedOrganizationRoles.totalCount : null;
  const dataSource = isLoaded ? data.paginatedOrganizationRoles.roles : [];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: string[]) =>
      setSelectedRowKeys(newSelectedRowKeys),
  };

  const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
    setSelectedRowKeys(
      e.target.checked ? dataSource.map((role) => role.id) : []
    );
  };

  return (
    <FilteredTable
      rowKey="id"
      title={
        rolesAccessEditionMode
          ? () => (
              <>
                <Checkbox
                  style={{ marginRight: 12 }}
                  indeterminate={
                    selectedRowKeys.length !== total &&
                    selectedRowKeys.length !== 0
                  }
                  checked={selectedRowKeys.length === total}
                  onChange={onCheckAllChange}
                />
                <Text weight="medium">
                  {selectedRowKeys.length}/{total} Available
                </Text>
              </>
            )
          : false
      }
      columns={columns}
      dataSource={dataSource}
      loading={!isLoaded}
      filtersDataReady={!!rolesData}
      pagination={{
        ...paginationVariables,
        total,
      }}
      onChange={() => {}}
      onFilterChange={filterChangeHandler}
      localStorageFilterKey={localStorageFilterKey}
      filters={filters}
      scroll={false}
      rowSelection={rolesAccessEditionMode ? rowSelection : false}
    />
  );
}
